import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useAuth } from "auth/AuthContext";
import { useAuthMachine } from "auth/state/hook";
import { CLUSTER_DETAIL_URL } from "../../utils/routePaths";
import awsLogo from "../../images/svg/chip/aws-logo.svg";
import azureLogo from "../../images/svg/chip/azure-logo.svg";
import gcpLogo from "../../images/svg/chip/gcp-logo.svg";
import SectionHeader from "../../shared/SectionHeader";
import { useFetchContext } from "../../utils/fetch";
import { useAuthHeaders } from "../../utils/query/query";
import {
  ClusterFacets,
  ClusterPage,
  ClusterStats,
  ClusterSummary,
  MissionControlFilters,
  fetchClusterFacets,
  fetchClusterPage,
  fetchClusterStats,
} from "./api";
import {
  COLUMN_KEYS,
  ColumnKey,
  DEFAULT_COLUMNS,
  SORTABLE_COLUMNS,
  cellValue,
  clusterNavigationPlan,
  toCsv,
} from "./columns";

/**
 * Mission Control: the fleet-wide, server-side paginated cluster listing (all organizations),
 * recreating V2's Flutter mission-control screen on the ported /api/cluster/page, /stats and
 * /facets endpoints. Unlike ClusterListings (own-org, client-side filtering), every filter, sort
 * and page here is computed by the database.
 *
 * Filter set, option values, default columns and CSV behaviour follow V2's screen so an operator
 * moving between the two sees the same tool. Two deliberate differences, both because V2's
 * version cannot work rather than as a redesign:
 *  - V2's "Tenant Type" filter is omitted: the listing's base WHERE fixes multi_tenant = false,
 *    so no multitenant row can ever be returned and the filter is a no-op there.
 *  - The healthy / needs-attention stats read "Coming soon", exactly as V2 shows them, because
 *    the backing computation matches no rows.
 */

const CLUSTER_TYPES = ["STARTER", "STANDARD", "ENTERPRISE", "TRIAL"];
const CLOUD_PROVIDERS = ["AWS", "AZURE", "GCP"];
const STATES = [
  "ASSIGNED",
  "AVAILABLE_FOR_TRIAL",
  "DELETED",
  "DELETING",
  "FAILED",
  "MODIFYING",
  "PROVISIONED",
  "PROVISIONING",
  "SCHEDULE_DELETION",
  "TRIAL_ENDED",
];
const ENVIRONMENT_TYPES = [
  "DEVELOPMENT",
  "ORKESTEST",
  "POC",
  "PRODUCTION",
  "STAGING",
  "TEST",
  "TRIAL",
];
const HOSTING = ["Orkes hosted", "Customer hosted"];
const VERSIONS = ["2.x", "3.x", "4.x", "5.x"];
const ACTIVE = ["True", "False"];

/** Card accents sampled from V2's mission-control screen: tint, icon and value share a hue. */
const CARD_TONES = {
  total: { tone: "#E8F1FD", accent: "#2F6FDB", value: "#1F4E9C" },
  hosting: { tone: "#F5EBFA", accent: "#8E4EC6", value: "#7A3E9D" },
  providers: { tone: "#FCF7E3", accent: "#B08900", value: "#8A7B1F" },
};

export default function MissionControl() {
  const context = useFetchContext();
  // The shared useFetch wrapper normally injects these; direct fetchers must pass them
  // explicitly or every call goes out unauthenticated (401).
  const authHeaders = useAuthHeaders();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [sort, setSort] = useState("name");
  const [direction, setDirection] = useState<"asc" | "desc">("asc");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // V2 opens the screen with these already applied (its route supplies
  // states=PROVISIONED, isActive=true, types=STARTER,STANDARD,ENTERPRISE), so the first view is
  // the live fleet rather than every historical row.
  const [types, setTypes] = useState<string[]>([
    "STARTER",
    "STANDARD",
    "ENTERPRISE",
  ]);
  const [regions, setRegions] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>(["PROVISIONED"]);
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const [clouds, setClouds] = useState<string[]>([]);
  const [envTypes, setEnvTypes] = useState<string[]>([]);
  const [active, setActive] = useState<string[]>(["True"]);
  const [hosting, setHosting] = useState<string[]>([]);
  const [conductorVersions, setConductorVersions] = useState<string[]>([]);
  const [workerVersions, setWorkerVersions] = useState<string[]>([]);

  const { authService } = useAuth();
  const [
    { currentOrganization, isSwitchingOrganization },
    { switchCurrentOrganization },
  ] = useAuthMachine(authService);
  const [columns, setColumns] = useState<ColumnKey[]>(DEFAULT_COLUMNS);
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  const filters = useMemo<MissionControlFilters>(
    () => ({
      search: debouncedSearch || undefined,
      types: types.length ? types : undefined,
      regions: regions.length ? regions : undefined,
      states: states.length ? states : undefined,
      orgIds: orgIds.length ? orgIds : undefined,
      cloudProviders: clouds.length ? clouds : undefined,
      environmentTypes: envTypes.length ? envTypes : undefined,
      hosting: hosting.length ? hosting : undefined,
      conductorVersions: conductorVersions.length
        ? conductorVersions
        : undefined,
      workerVersions: workerVersions.length ? workerVersions : undefined,
      // The backend takes a single Boolean, so selecting both True and False means "no constraint".
      active: active.length === 1 ? active[0] === "True" : undefined,
    }),
    [
      debouncedSearch,
      types,
      regions,
      states,
      orgIds,
      clouds,
      envTypes,
      hosting,
      conductorVersions,
      workerVersions,
      active,
    ]
  );

  const anyFilterActive =
    Boolean(debouncedSearch) ||
    [
      types,
      regions,
      states,
      orgIds,
      clouds,
      envTypes,
      hosting,
      conductorVersions,
      workerVersions,
      active,
    ].some((f) => f.length > 0);

  const pageQuery = useQuery<ClusterPage>(
    ["mission-control-page", page, size, sort, direction, filters],
    () =>
      fetchClusterPage(
        context,
        authHeaders,
        page,
        size,
        sort,
        direction,
        filters
      ),
    { keepPreviousData: true }
  );
  const statsQuery = useQuery<ClusterStats>(
    ["mission-control-stats", filters],
    () => fetchClusterStats(context, authHeaders, filters)
  );
  const facetsQuery = useQuery<ClusterFacets>(["mission-control-facets"], () =>
    fetchClusterFacets(context, authHeaders)
  );

  const onSort = (key: string) => {
    if (sort === key) {
      setDirection(direction === "asc" ? "desc" : "asc");
    } else {
      setSort(key);
      setDirection("asc");
    }
    setPage(0);
  };

  // Plain timeout-based debounce so typing does not fire a request per keystroke.
  const onSearchChange = (value: string) => {
    setSearch(value);
    window.clearTimeout((onSearchChange as any).t);
    (onSearchChange as any).t = window.setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 400);
  };

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setTypes([]);
    setRegions([]);
    setStates([]);
    setOrgIds([]);
    setClouds([]);
    setEnvTypes([]);
    setActive([]);
    setHosting([]);
    setConductorVersions([]);
    setWorkerVersions([]);
    setPage(0);
  };

  /**
   * Exports every row matching the current filters, not just the visible page, which is what V2
   * does: it re-requests page 0 with size = totalElements.
   */
  const downloadCsv = async () => {
    setExporting(true);
    try {
      const total = pageQuery.data?.totalElements ?? 0;
      const all = await fetchClusterPage(
        context,
        authHeaders,
        0,
        total > 0 ? total : 100000,
        sort,
        direction,
        filters
      );
      const rows = [
        columns as string[],
        ...all.content.map((c) => columns.map((key) => cellValue(c, key))),
      ];
      const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "clusters-list.csv";
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const toggleColumn = (key: ColumnKey) => {
    setColumns((current) =>
      current.includes(key)
        ? current.filter((c) => c !== key)
        : // Keep V2's column order regardless of the order they were switched on.
          COLUMN_KEYS.filter((c) => current.includes(c) || c === key)
    );
  };

  /**
   * Same-organization clusters are a plain link. Cross-organization ones need that organization
   * made active first, because the details page only ever fetches within the active one.
   *
   * The switch itself does the navigating: on success the auth machine runs navigateToHome, which
   * honours a one-shot `urlToNavigate` in localStorage and clears it after use. Handing it the
   * cluster page is what makes this work, because Mission Control unmounts during the switch, so
   * component state cannot carry the destination across it.
   */
  const onClusterClick = (event: React.MouseEvent, cluster: ClusterSummary) => {
    const plan = clusterNavigationPlan(
      cluster.organizationId,
      currentOrganization?.id
    );
    if (!plan.needsOrganizationSwitch || !plan.organizationId) {
      return;
    }
    event.preventDefault();
    localStorage.setItem(
      "urlToNavigate",
      `${CLUSTER_DETAIL_URL.ROOT}/${cluster.name}`
    );
    switchCurrentOrganization(plan.organizationId);
  };

  const stats = statsQuery.data;
  const rows = pageQuery.data?.content ?? [];
  const organizations = facetsQuery.data?.organizations ?? [];

  return (
    <Box sx={{ padding: 5, paddingTop: 1.5 }}>
      <Helmet>
        <title>Mission Control</title>
      </Helmet>
      <SectionHeader
        title={"Mission Control"}
        _deprecate_marginTop={0}
        style={{ padding: "20px 0 4px" }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your central hub for real-time monitoring and insights.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard
          label="Total Clusters"
          tone={CARD_TONES.total.tone}
          icon={
            <CloudOutlinedIcon
              fontSize="medium"
              sx={{ color: CARD_TONES.total.accent }}
            />
          }
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: CARD_TONES.total.value }}
          >
            {stats?.totalClusters ?? "…"}
          </Typography>
        </StatCard>
        <StatCard
          label="Hosting"
          tone={CARD_TONES.hosting.tone}
          icon={
            <DonutLargeOutlinedIcon
              fontSize="medium"
              sx={{ color: CARD_TONES.hosting.accent }}
            />
          }
        >
          <Stack direction="row" justifyContent="space-between" sx={{ pr: 1 }}>
            <SplitValue
              value={stats?.orkesHosted}
              caption="Orkes"
              color={CARD_TONES.hosting.value}
            />
            <SplitValue
              value={stats?.customerHosted}
              caption="Customer"
              color={CARD_TONES.hosting.value}
            />
          </Stack>
        </StatCard>
        <StatCard
          label="Cloud Providers"
          tone={CARD_TONES.providers.tone}
          icon={
            <LayersOutlinedIcon
              fontSize="medium"
              sx={{ color: CARD_TONES.providers.accent }}
            />
          }
        >
          <Stack direction="row" justifyContent="space-between" sx={{ pr: 1 }}>
            <SplitValue
              value={stats?.aws}
              caption={<ProviderLogo provider="AWS" />}
              color={CARD_TONES.providers.value}
            />
            <SplitValue
              value={stats?.azure}
              caption={<ProviderLogo provider="AZURE" />}
              color={CARD_TONES.providers.value}
            />
            <SplitValue
              value={stats?.gcp}
              caption={<ProviderLogo provider="GCP" />}
              color={CARD_TONES.providers.value}
            />
          </Stack>
        </StatCard>
      </Box>

      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Clusters Data</Typography>
            {isSwitchingOrganization && (
              <Typography variant="caption" color="text.secondary">
                Switching organization…
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              variant="standard"
              placeholder="Search..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              sx={{ minWidth: 240 }}
              inputProps={{ "data-testid": "mc-search" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="small"
              startIcon={<ViewWeekOutlinedIcon fontSize="small" />}
              onClick={(e) => setColumnsAnchor(e.currentTarget)}
              data-testid="mc-columns-button"
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsAnchor}
              open={Boolean(columnsAnchor)}
              onClose={() => setColumnsAnchor(null)}
            >
              <MenuItem onClick={() => setColumns(DEFAULT_COLUMNS)}>
                <ListItemText primary="Reset to Default" />
              </MenuItem>
              {COLUMN_KEYS.map((key) => (
                <MenuItem key={key} onClick={() => toggleColumn(key)} dense>
                  <Checkbox size="small" checked={columns.includes(key)} />
                  <ListItemText primary={key} />
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 2,
          }}
        >
          <MultiFilter
            label="Cluster Type"
            value={types}
            options={CLUSTER_TYPES}
            onChange={(v) => {
              setTypes(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="Region"
            value={regions}
            options={facetsQuery.data?.regions ?? []}
            onChange={(v) => {
              setRegions(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="State"
            value={states}
            options={STATES}
            onChange={(v) => {
              setStates(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="Organizations"
            value={orgIds}
            options={organizations.map((o) => o.id)}
            labelFor={(id) =>
              organizations.find((o) => o.id === id)?.name ?? id
            }
            onChange={(v) => {
              setOrgIds(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="Cloud Provider"
            value={clouds}
            options={CLOUD_PROVIDERS}
            onChange={(v) => {
              setClouds(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="Environment Type"
            value={envTypes}
            options={ENVIRONMENT_TYPES}
            onChange={(v) => {
              setEnvTypes(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="Active"
            value={active}
            options={ACTIVE}
            onChange={(v) => {
              setActive(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="Hosting"
            value={hosting}
            options={HOSTING}
            onChange={(v) => {
              setHosting(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="Conductor Version"
            value={conductorVersions}
            options={VERSIONS}
            onChange={(v) => {
              setConductorVersions(v);
              setPage(0);
            }}
          />
          <MultiFilter
            label="Worker Version"
            value={workerVersions}
            options={VERSIONS}
            onChange={(v) => {
              setWorkerVersions(v);
              setPage(0);
            }}
          />
        </Box>

        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FilterAltOffOutlinedIcon fontSize="small" />}
            onClick={clearFilters}
            disabled={!anyFilterActive}
            data-testid="mc-clear-filters"
          >
            Clear Filters
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={downloadCsv}
            disabled={exporting || rows.length === 0}
            data-testid="mc-download-csv"
          >
            {exporting ? "Preparing…" : "Download CSV"}
          </Button>
        </Stack>
      </Card>

      {pageQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Card}>
          <Table size="small" data-testid="mc-table">
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>
                    {SORTABLE_COLUMNS.includes(key) ? (
                      <TableSortLabel
                        active={sort === key}
                        direction={sort === key ? direction : "asc"}
                        onClick={() => onSort(key)}
                      >
                        {key}
                      </TableSortLabel>
                    ) : (
                      key
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((cluster) => (
                <TableRow key={cluster.id} hover>
                  {columns.map((key) => (
                    <TableCell key={key}>
                      <CellContent
                        cluster={cluster}
                        columnKey={key}
                        onClusterClick={onClusterClick}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ p: 3 }}
                    >
                      No clusters match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pageQuery.data?.totalElements ?? 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}
    </Box>
  );
}

/** State and type read as chips in V2; everything else is plain text from the shared accessor. */
function CellContent({
  cluster,
  columnKey,
  onClusterClick,
}: {
  cluster: ClusterSummary;
  columnKey: ColumnKey;
  onClusterClick?: (event: React.MouseEvent, cluster: ClusterSummary) => void;
}) {
  const value = cellValue(cluster, columnKey);
  if (columnKey === "state") {
    return (
      <Chip
        size="small"
        label={value || "-"}
        color={
          value === "PROVISIONED"
            ? "success"
            : value === "FAILED"
            ? "error"
            : "default"
        }
      />
    );
  }
  if (columnKey === "type") {
    return <Chip size="small" variant="outlined" label={value || "-"} />;
  }
  if (columnKey === "cloudProvider") {
    return value ? <ProviderLogo provider={value} /> : <>-</>;
  }
  if (columnKey === "name") {
    // V2 makes the cluster name the way into the cluster page. Mission Control spans
    // organizations while the details page fetches within the active one, so opening a cluster
    // from another organization surfaces that page's "Could not fetch cluster" error, which is
    // the same outcome V2 gives.
    return (
      <Link
        to={`${CLUSTER_DETAIL_URL.ROOT}/${value}`}
        style={{ fontWeight: 600 }}
        data-testid="mc-cluster-link"
        onClick={(event) => onClusterClick?.(event, cluster)}
      >
        {value}
      </Link>
    );
  }
  return <>{value}</>;
}

function StatCard({
  label,
  tone,
  icon,
  children,
}: {
  label: string;
  tone: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        backgroundColor: tone,
        borderColor: "transparent",
        borderRadius: 2,
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        {icon}
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
          {label}
        </Typography>
      </Stack>
      <Box
        sx={{ mt: 1.5, flexGrow: 1, display: "flex", alignItems: "flex-end" }}
      >
        <Box sx={{ width: "100%" }}>{children}</Box>
      </Box>
    </Card>
  );
}

/** Brand mark for a provider, falling back to nothing for an unrecognized value. */
function ProviderLogo({ provider }: { provider: string }) {
  const logo =
    provider === "AWS"
      ? awsLogo
      : provider === "AZURE"
      ? azureLogo
      : provider === "GCP"
      ? gcpLogo
      : null;
  if (!logo) {
    return null;
  }
  return (
    <img
      src={logo}
      alt={provider}
      height={22}
      style={{ display: "block", maxWidth: 34, objectFit: "contain" }}
    />
  );
}

function SplitValue({
  value,
  caption,
  color,
}: {
  value: number | undefined;
  caption: React.ReactNode;
  color?: string;
}) {
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ color }}>
        {value ?? "…"}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.secondary"
        component="div"
        sx={{ mt: 0.5 }}
      >
        {caption}
      </Typography>
    </Box>
  );
}

/**
 * Multi-select with the selection shown as chips, matching V2's dropdowns. Options carry the
 * value sent to the backend; labelFor renders something friendlier where those differ (the
 * Organizations filter sends ids but shows names).
 */
function MultiFilter({
  label,
  value,
  options,
  onChange,
  labelFor,
}: {
  label: string;
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
  labelFor?: (option: string) => string;
}) {
  const display = labelFor ?? ((option: string) => option);
  return (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) =>
          onChange(
            typeof e.target.value === "string"
              ? e.target.value.split(",")
              : (e.target.value as string[])
          )
        }
        input={<OutlinedInput label={label} />}
        inputProps={{ "data-testid": `mc-filter-${label}` }}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(selected as string[]).map((v) => (
              <Chip key={v} size="small" label={display(v)} />
            ))}
          </Box>
        )}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option} dense>
            <Checkbox size="small" checked={value.includes(option)} />
            <ListItemText primary={display(option)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
